/**
 * Event_Store append-only con cadena hash de la Capa Operacional AI-First.
 *
 * Implementa la interfaz {@link EventStore} de `../api/contracts` para la
 * variante **demo** (client-side, R17.1/R17.5), sobre el almacén
 * append-only {@link AppendOnlyStore} de `./idb` (IndexedDB en navegador,
 * **fallback en memoria** en Node/Vitest/jsdom). El Event_Store:
 *
 *   - registra cada Evento_Operacional como hecho inmutable; **no expone**
 *     `update` ni `delete` (append-only, R9.1, R9.2);
 *   - asigna una `seq` monótona **por `entidad_ref`** (R9.5);
 *   - calcula `payload_hash = SHA-256(prev_hash ‖ canonical(e))` con
 *     `crypto.subtle` (con fallback SHA-256 puro), encadenando cada evento
 *     con el hash del anterior — `prev_hash_{i+1} = payload_hash_i` (R9.3);
 *   - reconstruye el estado de una entidad por `replay` determinista en
 *     orden temporal no decreciente (R9.4);
 *   - permite iterar el log global con `streamFrom` (base del fan-out
 *     realtime y de la re-sync, R7.7/R7.8);
 *   - verifica la integridad de la cadena con `verifyChain` (R9.3, R9.7);
 *   - rechaza y **audita** todo intento de mutación/borrado de un evento
 *     existente, preservándolo intacto (R9.6).
 *
 * Las funciones puras {@link canonical}, {@link canonicalEvento},
 * {@link hashEvento} y {@link verifyChain} (sobre un array de eventos) se
 * exportan para que las tareas de testing PBT (Properties 5, 24, 25) las
 * ejerciten **sin IndexedDB real** ni instanciar la clase.
 */

import type {
  AppendResult,
  ChainStatus,
  EntidadRef,
  EstadoEntidad,
  EventStore,
  EventoOperacional,
  Id,
  IsoTimestamp,
  RangoTemporal,
  Result,
} from "../api/contracts";

import { type AppendOnlyStore, openAppendOnlyStore } from "./idb";

// ---------------------------------------------------------------------------
// Tipos auxiliares
// ---------------------------------------------------------------------------

/**
 * Datos de entrada de {@link EventStore.append}: un Evento_Operacional sin
 * los campos que el Event_Store **deriva** (identidad, secuencia y cadena
 * hash). Coincide con la firma del contrato.
 */
export type EventoEntrada = Omit<
  EventoOperacional,
  "event_id" | "seq" | "prev_hash" | "payload_hash"
>;

/** Clave estable de una entidad para indexar la `seq` monótona por entidad. */
function entityKey(ref: EntidadRef): string {
  return `${ref.tipo}:${ref.id}`;
}

/** Instante actual en ISO-8601 UTC. */
function nowIso(): IsoTimestamp {
  return new Date().toISOString();
}

/** Genera un identificador único (UUID si está disponible). */
function generarId(): Id {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch {
    /* fallback abajo */
  }
  return `id-${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

function mensajeError(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ---------------------------------------------------------------------------
// canonical — serialización determinista (orden de claves lexicográfico)
// ---------------------------------------------------------------------------

/**
 * Reescritura recursiva de un valor con las claves de cada objeto
 * ordenadas lexicográficamente, de modo que dos objetos equivalentes
 * produzcan exactamente la misma representación.
 */
function canonicalize(value: unknown): unknown {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(canonicalize);
  const obj = value as Record<string, unknown>;
  const ordenado: Record<string, unknown> = {};
  for (const clave of Object.keys(obj).sort()) {
    const v = obj[clave];
    if (v === undefined) continue; // omitido, como en JSON
    ordenado[clave] = canonicalize(v);
  }
  return ordenado;
}

/**
 * `canonical(e)`: forma canónica **determinista** de cualquier valor
 * serializable (orden de claves lexicográfico recursivo). Las marcas de
 * tiempo se asumen ya normalizadas a ISO-8601 UTC en los eventos. Misma
 * entrada → misma cadena, independiente del orden de inserción de claves
 * (R9.3).
 */
export function canonical(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

/**
 * Forma canónica de un Evento_Operacional **excluyendo** los campos de la
 * cadena hash (`prev_hash`, `payload_hash`): son el enlace y la salida del
 * cálculo, no parte de la entrada canónica. El resto del evento
 * (`event_id`, `tipo`, `entidad_ref`, `actor`, `ts`, `payload`, `seq`,
 * `correlation_id`) determina su identidad de contenido.
 */
export function canonicalEvento(
  e: EventoEntrada & { event_id?: Id; seq?: number },
): string {
  const sinHash = { ...(e as Record<string, unknown>) };
  delete sinHash.prev_hash;
  delete sinHash.payload_hash;
  return canonical(sinHash);
}

// ---------------------------------------------------------------------------
// SHA-256 — crypto.subtle con fallback puro
// ---------------------------------------------------------------------------

function getSubtle(): SubtleCrypto | null {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    return c && c.subtle ? c.subtle : null;
  } catch {
    return null;
  }
}

function bytesToHex(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

/** SHA-256 puro (fallback cuando `crypto.subtle` no está disponible). */
function sha256Fallback(bytes: Uint8Array): string {
  const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]);
  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

  const l = bytes.length;
  const bitLen = l * 8;
  const total = (l + 1 + 8 + 63) & ~63; // múltiplo de 64 ≥ l + 1 + 8
  const msg = new Uint8Array(total);
  msg.set(bytes);
  msg[l] = 0x80;
  const dv = new DataView(msg.buffer);
  dv.setUint32(total - 4, bitLen >>> 0, false);
  dv.setUint32(total - 8, Math.floor(bitLen / 0x100000000), false);

  const w = new Uint32Array(64);
  const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

  for (let i = 0; i < total; i += 64) {
    for (let t = 0; t < 16; t++) w[t] = dv.getUint32(i + t * 4, false);
    for (let t = 16; t < 64; t++) {
      const s0 = rotr(w[t - 15], 7) ^ rotr(w[t - 15], 18) ^ (w[t - 15] >>> 3);
      const s1 = rotr(w[t - 2], 17) ^ rotr(w[t - 2], 19) ^ (w[t - 2] >>> 10);
      w[t] = (w[t - 16] + s0 + w[t - 7] + s1) | 0;
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let t = 0; t < 64; t++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + S1 + ch + K[t] + w[t]) | 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) | 0;
      h = g; g = f; f = e; e = (d + temp1) | 0;
      d = c; c = b; b = a; a = (temp1 + temp2) | 0;
    }
    h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0; h5 = (h5 + f) | 0; h6 = (h6 + g) | 0; h7 = (h7 + h) | 0;
  }

  const hex = (x: number): string => (x >>> 0).toString(16).padStart(8, "0");
  return hex(h0) + hex(h1) + hex(h2) + hex(h3) + hex(h4) + hex(h5) + hex(h6) + hex(h7);
}

/** Digest SHA-256 hexadecimal de una cadena (UTF-8). */
async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const subtle = getSubtle();
  if (subtle) {
    try {
      const digest = await subtle.digest("SHA-256", bytes);
      return bytesToHex(new Uint8Array(digest));
    } catch {
      /* fallback abajo */
    }
  }
  return sha256Fallback(bytes);
}

/**
 * Calcula `payload_hash = SHA-256(prev_hash ‖ canonical(e))` de un evento
 * dado su `prev_hash` (null para el primero de la cadena). El resultado se
 * prefija con `"sha256:"` para distinguir el algoritmo (R9.3).
 */
export async function hashEvento(
  prev_hash: string | null,
  e: EventoEntrada & { event_id?: Id; seq?: number },
): Promise<string> {
  const entrada = `${prev_hash ?? ""}\u2016${canonicalEvento(e)}`;
  return `sha256:${await sha256Hex(entrada)}`;
}

// ---------------------------------------------------------------------------
// verifyChain — verificación pura sobre un array de eventos
// ---------------------------------------------------------------------------

/**
 * Verifica la integridad de la cadena hash de un array de Eventos_
 * Operacionales **en orden de inserción** (R9.3, R9.7, Property 24/25):
 *
 *   1. cada `payload_hash` recomputa exactamente `SHA-256(prev_hash ‖
 *      canonical(e))`;
 *   2. el enlace `prev_hash_{i+1} = payload_hash_i` se preserva;
 *   3. la `seq` es estrictamente monótona por `entidad_ref`.
 *
 * Devuelve {@link ChainStatus} con `valido=false` y la posición del primer
 * eslabón roto ante cualquier inconsistencia.
 */
export async function verifyChain(eventos: EventoOperacional[]): Promise<ChainStatus> {
  let prev: string | null = null;
  const seqPorEntidad = new Map<string, number>();

  for (let i = 0; i < eventos.length; i++) {
    const e = eventos[i];

    // 1) Enlace con el evento previo de la cadena.
    if (i > 0 && e.prev_hash !== prev) {
      return { valido: false, primer_error: i, detalle: `prev_hash inconsistente en la posición ${i}` };
    }

    // 2) Recomputar el payload_hash y comparar.
    const esperado = await hashEvento(e.prev_hash, e);
    if (esperado !== e.payload_hash) {
      return { valido: false, primer_error: i, detalle: `payload_hash inválido en la posición ${i}` };
    }

    // 3) seq estrictamente monótona por entidad (R9.5).
    const key = entityKey(e.entidad_ref);
    const ultima = seqPorEntidad.get(key) ?? 0;
    if (e.seq <= ultima) {
      return {
        valido: false,
        primer_error: i,
        detalle: `seq no monótona para ${key} en la posición ${i} (${e.seq} ≤ ${ultima})`,
      };
    }
    seqPorEntidad.set(key, e.seq);

    prev = e.payload_hash;
  }

  return { valido: true };
}

// ---------------------------------------------------------------------------
// replay — reconstrucción determinista del estado de una entidad
// ---------------------------------------------------------------------------

/** Compara dos instantes ISO-8601; desempata por `seq` ascendente (R9.4). */
function compararTemporal(a: EventoOperacional, b: EventoOperacional): number {
  const ta = Date.parse(a.ts);
  const tb = Date.parse(b.ts);
  if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
  if (a.ts !== b.ts) return a.ts < b.ts ? -1 : 1;
  return a.seq - b.seq;
}

/**
 * Reproduce de forma **determinista** los eventos de una entidad con
 * `ts ≤ hasta_ts`, en orden temporal no decreciente, plegando los payloads
 * en un estado materializado. El mismo conjunto de eventos produce el mismo
 * estado, independiente del orden de llegada (R9.4, Property 5).
 */
export function replayEventos(
  eventos: EventoOperacional[],
  entidad_ref: EntidadRef,
  hasta_ts: IsoTimestamp,
): EstadoEntidad {
  const key = entityKey(entidad_ref);
  const limite = Date.parse(hasta_ts);

  const relevantes = eventos
    .filter((ev) => {
      if (entityKey(ev.entidad_ref) !== key) return false;
      const t = Date.parse(ev.ts);
      if (Number.isFinite(limite) && Number.isFinite(t)) return t <= limite;
      return ev.ts <= hasta_ts;
    })
    .sort(compararTemporal);

  const estado: Record<string, unknown> = {};
  let ultima_seq = 0;
  for (const ev of relevantes) {
    Object.assign(estado, ev.payload);
    estado.__ultimo_tipo = ev.tipo;
    estado.__ultimo_ts = ev.ts;
    ultima_seq = ev.seq;
  }

  return { entidad_ref, hasta_ts, estado, ultima_seq };
}

// ---------------------------------------------------------------------------
// EventStoreLocal — implementación demo de la interfaz EventStore
// ---------------------------------------------------------------------------

/** Opciones de construcción del {@link EventStoreLocal}. */
export interface EventStoreLocalOptions {
  /** Almacén append-only a usar (inyectable en tests). Por defecto IndexedDB/memoria. */
  store?: AppendOnlyStore<EventoOperacional>;
  /** Nombre del object store en IndexedDB. */
  storeName?: string;
}

/**
 * Event_Store append-only client-side que implementa {@link EventStore}.
 * Mantiene en memoria una caché en orden de inserción (= orden de cadena)
 * y los índices `seq` por entidad y `last_hash` global para encadenar y
 * verificar sin re-leer todo el almacén en cada operación.
 */
export class EventStoreLocal implements EventStore {
  private store: AppendOnlyStore<EventoOperacional> | null;
  private readonly storeName: string;
  private cargado = false;
  /** Caché en orden de inserción (índice 0 → seq_global 1). */
  private readonly cache: EventoOperacional[] = [];
  /** Última `seq` asignada por entidad (R9.5). */
  private readonly seqPorEntidad = new Map<string, number>();
  /** `payload_hash` del último evento de la cadena global (null si vacía). */
  private ultimoHash: string | null = null;

  constructor(opts?: EventStoreLocalOptions) {
    this.store = opts?.store ?? null;
    this.storeName = opts?.storeName ?? "eventos";
  }

  /** Abre el almacén (perezoso) y reconstruye índices desde lo persistido. */
  private async asegurar(): Promise<AppendOnlyStore<EventoOperacional>> {
    if (!this.store) {
      this.store = await openAppendOnlyStore<EventoOperacional>(this.storeName);
    }
    if (!this.cargado) {
      const registros = await this.store.getAll();
      for (const r of registros) this.indexar(r.value);
      this.cargado = true;
    }
    return this.store;
  }

  /** Añade un evento ya construido a la caché y actualiza los índices. */
  private indexar(e: EventoOperacional): void {
    this.cache.push(e);
    const key = entityKey(e.entidad_ref);
    const ultima = this.seqPorEntidad.get(key) ?? 0;
    if (e.seq > ultima) this.seqPorEntidad.set(key, e.seq);
    this.ultimoHash = e.payload_hash;
  }

  /**
   * Registra un Evento_Operacional: asigna `seq` monótona por entidad,
   * calcula la cadena hash y persiste (append-only, R9.1, R9.3, R9.5).
   */
  async append(e: EventoEntrada): Promise<Result<AppendResult>> {
    try {
      const store = await this.asegurar();
      const key = entityKey(e.entidad_ref);
      const seq = (this.seqPorEntidad.get(key) ?? 0) + 1;
      const prev_hash = this.ultimoHash;
      const event_id = generarId();

      const parcial = {
        event_id,
        tipo: e.tipo,
        entidad_ref: e.entidad_ref,
        actor: e.actor,
        ts: e.ts,
        payload: e.payload,
        seq,
        correlation_id: e.correlation_id,
      };
      const payload_hash = await hashEvento(prev_hash, parcial);
      const completo: EventoOperacional = { ...parcial, prev_hash, payload_hash };

      await store.add(completo);
      this.indexar(completo);

      return { ok: true, value: { event_id, seq, hash: payload_hash, prev_hash } };
    } catch (err) {
      return {
        ok: false,
        error: `No se pudo registrar el Evento_Operacional: ${mensajeError(err)}`,
        degraded: true,
      };
    }
  }

  /**
   * Reconstruye el estado de una entidad reproduciendo sus eventos hasta
   * `hasta_ts` en orden temporal no decreciente (R9.4).
   */
  async replay(entidad_ref: EntidadRef, hasta_ts: IsoTimestamp): Promise<Result<EstadoEntidad>> {
    try {
      await this.asegurar();
      return { ok: true, value: replayEventos(this.cache, entidad_ref, hasta_ts) };
    } catch (err) {
      return { ok: false, error: `No se pudo reconstruir el estado: ${mensajeError(err)}`, degraded: true };
    }
  }

  /**
   * Itera el log global desde una `seq_global` (1-based) en orden de
   * inserción; base del fan-out realtime y la re-sincronización (R7.7, R7.8).
   */
  async *streamFrom(seq_global: number): AsyncIterable<EventoOperacional> {
    await this.asegurar();
    const inicio = Math.max(0, Math.floor(seq_global) - 1);
    for (let i = inicio; i < this.cache.length; i++) {
      yield this.cache[i];
    }
  }

  /**
   * Verifica la integridad de la cadena hash de los eventos cuyo `ts` cae
   * dentro del rango, en orden de inserción (R9.3, R9.7). La verificación
   * recomputa cada `payload_hash` y comprueba los enlaces y la monotonía de
   * `seq` por entidad.
   */
  async verifyChain(rango: RangoTemporal): Promise<Result<ChainStatus>> {
    try {
      await this.asegurar();
      const desde = Date.parse(rango.inicio);
      const hasta = Date.parse(rango.fin);
      const enRango = this.cache.filter((ev) => {
        const t = Date.parse(ev.ts);
        if (Number.isFinite(t) && Number.isFinite(desde) && Number.isFinite(hasta)) {
          return t >= desde && t <= hasta;
        }
        return ev.ts >= rango.inicio && ev.ts <= rango.fin;
      });
      const status = await verifyChain(enRango);
      return { ok: true, value: status };
    } catch (err) {
      return { ok: false, error: `No se pudo verificar la cadena: ${mensajeError(err)}`, degraded: true };
    }
  }

  // -------------------------------------------------------------------------
  // Append-only: rechazo + auditoría de intentos de mutación/borrado (R9.6)
  // -------------------------------------------------------------------------

  /**
   * El Event_Store es **append-only**: no expone `update` ni `delete`. Todo
   * intento de modificar o borrar un Evento_Operacional existente se
   * **rechaza**, el evento original se **preserva intacto** y el intento se
   * **registra** como Evento_Operacional de auditoría (R9.6).
   *
   * Esta es la única vía para señalar la intención de mutar; nunca altera
   * eventos persistidos.
   */
  async rejectMutation(intento: {
    entidad_ref: EntidadRef;
    actor: string;
    operacion: "update" | "delete";
    target_event_id?: Id;
  }): Promise<Result<never>> {
    await this.append({
      tipo: "INTENTO_MUTACION_RECHAZADO",
      entidad_ref: intento.entidad_ref,
      actor: intento.actor,
      ts: nowIso(),
      payload: {
        operacion: intento.operacion,
        target_event_id: intento.target_event_id ?? null,
        resultado: "rechazado",
        motivo: "El Event_Store es append-only (R9.2).",
      },
      correlation_id: generarId(),
    });
    return {
      ok: false,
      error: `Operación '${intento.operacion}' rechazada: el Event_Store es append-only; el evento se preserva intacto y el intento queda auditado (R9.2, R9.6).`,
      degraded: false,
    };
  }

  /** Número de eventos registrados (uso de diagnóstico/test). */
  async size(): Promise<number> {
    await this.asegurar();
    return this.cache.length;
  }

  /** Snapshot del log global en orden de inserción (uso de diagnóstico/test). */
  async snapshot(): Promise<EventoOperacional[]> {
    await this.asegurar();
    return [...this.cache];
  }
}
